import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Popover,
  Tooltip,
  Tabs,
  Tab,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import { ValidationRules, validateForm, hasErrors, formatErrors } from '../utils/validation';
import { createTooltipProps } from '../utils/fieldTooltips';
import MissingSelectOption from '../components/MissingSelectOption';

// ValidatedFormControl defined outside to prevent recreation on every render
const ValidatedFormControl = React.memo(
  ({
    name,
    label,
    required,
    children,
    tooltipDescription,
    validationErrors = {},
    formData = {},
    handleChange,
    ...props
  }) => {
    const hasError = validationErrors[name];
    const popover = hasError ? (
      <Popover id={`popover-${name}`}>
        <Popover.Header as="h3">Validation Error</Popover.Header>
        <Popover.Body>{validationErrors[name]}</Popover.Body>
      </Popover>
    ) : null;

    const tooltip = tooltipDescription ? (
      <Tooltip id={`tooltip-${name}`}>{tooltipDescription}</Tooltip>
    ) : null;

    const formControl = children || (
      <Form.Control
        {...props}
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        isInvalid={hasError}
      />
    );

    const wrappedControl = hasError ? (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popover}>
        {formControl}
      </OverlayTrigger>
    ) : tooltipDescription ? (
      <OverlayTrigger placement="top" overlay={tooltip}>
        {formControl}
      </OverlayTrigger>
    ) : (
      formControl
    );

    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        {wrappedControl}
      </Form.Group>
    );
  }
);

function CustomerSiteModal({ customerSite, onClose, onSave }) {
  const [modalError, setModalError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    account_no: '',
    site_name: '',
    line_1: '',
    line_2: '',
    line_3: '',
    town: '',
    county: '',
    postcode: '',
    telephone: '',
    fax: '',
    delivery_directions: '',
    start_date: '',
    end_date: '',
    latitude: '',
    longitude: '',
    area: '',
    route: '',
    depot: '',
    active: true,
  });
  const [customers, setCustomers] = useState([]);
  const [depots, setDepots] = useState([]);

  useEffect(() => {
    if (customerSite) {
      setFormData({
        account_no: customerSite.account_no || '',
        site_name: customerSite.site_name || '',
        line_1: customerSite.line_1 || '',
        line_2: customerSite.line_2 || '',
        line_3: customerSite.line_3 || '',
        town: customerSite.town || '',
        county: customerSite.county || '',
        postcode: customerSite.postcode || '',
        telephone: customerSite.telephone || '',
        fax: customerSite.fax || '',
        delivery_directions: customerSite.delivery_directions || '',
        start_date: customerSite.start_date || '',
        end_date: customerSite.end_date || '',
        latitude: customerSite.latitude || '',
        longitude: customerSite.longitude || '',
        area: customerSite.area || '',
        route: customerSite.route || '',
        depot: customerSite.depot || '',
        active: customerSite.active !== undefined ? customerSite.active : true,
      });
    } else {
      // Reset form for new customer site
      setFormData({
        account_no: '',
        site_name: '',
        line_1: '',
        line_2: '',
        line_3: '',
        town: '',
        county: '',
        postcode: '',
        telephone: '',
        fax: '',
        delivery_directions: '',
        start_date: '',
        end_date: '',
        latitude: '',
        longitude: '',
        area: '',
        route: '',
        depot: '',
        active: true,
      });
    }
  }, [customerSite]);

  useEffect(() => {
    // Fetch customers for dropdown
    const fetchCustomers = async () => {
      try {
        const response = await axios.get('/api/customers?limit=1000');
        // Extract unique account numbers and names from customers
        const customerData = response.data.customers || response.data || [];
        setCustomers(customerData);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      }
    };

    const fetchDepots = async () => {
      try {
        const response = await axios.get('/api/depots?limit=1000');
        setDepots(response.data.depots || []);
      } catch (error) {
        console.error('Error fetching depots:', error);
      }
    };

    fetchCustomers();
    fetchDepots();
  }, []);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const updatedFormData = {
      ...formData,
      [e.target.name]: value,
    };

    setFormData(updatedFormData);

    // Clear validation errors for this field when user starts typing
    if (validationErrors[e.target.name]) {
      const newErrors = { ...validationErrors };
      delete newErrors[e.target.name];
      setValidationErrors(newErrors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError(''); // Clear any previous errors

    // Comprehensive validation using validation rules
    const errors = validateForm(formData, ValidationRules.customerSite);
    setValidationErrors(errors);

    if (hasErrors(errors)) {
      setModalError(formatErrors(errors));
      return;
    }

    try {
      await onSave(formData);
      // If successful, onSave should close the modal
    } catch (error) {
      console.error('Error in modal submit:', error);
      setModalError(error.response?.data?.error || error.message || 'Failed to save customer site');
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>{customerSite ? 'Edit Customer Site' : 'Add New Customer Site'}</Modal.Title>
      </Modal.Header>

      {modalError && (
        <div className="alert alert-danger mx-3 mt-3 mb-0" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {modalError}
        </div>
      )}

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Tabs defaultActiveKey="site" className="mb-3">
            <Tab eventKey="site" title="Site">
              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="account_no"
                    label="Account No"
                    required={true}
                    tooltipDescription="Customer account number for site association"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  >
                    <Form.Select
                      name="account_no"
                      value={formData.account_no}
                      onChange={handleChange}
                      isInvalid={!!validationErrors.account_no}
                    >
                      <option value="">Select a customer...</option>
                      <MissingSelectOption
                        value={formData.account_no}
                        options={customers}
                        getOptionValue={(customer) => customer.account_no}
                      />
                      {customers.map((customer) => (
                        <option key={customer.account_no} value={customer.account_no}>
                          {customer.account_no} - {customer.name}
                        </option>
                      ))}
                    </Form.Select>
                  </ValidatedFormControl>
                </Col>
                <Col md={6}>
                  <ValidatedFormControl
                    name="site_name"
                    label="Site Name"
                    required={true}
                    type="text"
                    maxLength={50}
                    tooltipDescription="Name of the customer site location"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="address" title="Address &amp; Contact">
              <Row>
                <Col md={4}>
                  <ValidatedFormControl
                    name="line_1"
                    label="Line 1"
                    type="text"
                    tooltipDescription="Primary address line"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={4}>
                  <ValidatedFormControl
                    name="line_2"
                    label="Line 2"
                    type="text"
                    tooltipDescription="Secondary address line"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={4}>
                  <ValidatedFormControl
                    name="line_3"
                    label="Line 3"
                    type="text"
                    tooltipDescription="Tertiary address line"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Town</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip {...createTooltipProps('town')}>Town or city name</Tooltip>}
                    >
                      <Form.Control
                        type="text"
                        name="town"
                        value={formData.town}
                        onChange={handleChange}
                        {...createTooltipProps('town')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>County</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip {...createTooltipProps('county')}>County or state</Tooltip>}
                    >
                      <Form.Control
                        type="text"
                        name="county"
                        value={formData.county}
                        onChange={handleChange}
                        {...createTooltipProps('county')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postcode</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('postcode')}>Postal or ZIP code</Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleChange}
                        {...createTooltipProps('postcode')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telephone</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('telephone')}>
                          Site telephone number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        {...createTooltipProps('telephone')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fax</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip {...createTooltipProps('fax')}>Site fax number</Tooltip>}
                    >
                      <Form.Control
                        type="text"
                        name="fax"
                        value={formData.fax}
                        onChange={handleChange}
                        {...createTooltipProps('fax')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Active</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('active')}>
                          Controls whether site is active
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        name="active"
                        checked={formData.active}
                        onChange={handleChange}
                        className="mt-2"
                        {...createTooltipProps('active')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Delivery Directions</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('delivery_directions')}>
                          Special delivery instructions for this site
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="delivery_directions"
                        value={formData.delivery_directions}
                        onChange={handleChange}
                        {...createTooltipProps('delivery_directions')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="logistics" title="Logistics">
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('start_date')}>Site start date</Tooltip>
                      }
                    >
                      <Form.Control
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        {...createTooltipProps('start_date')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip {...createTooltipProps('end_date')}>Site end date</Tooltip>}
                    >
                      <Form.Control
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        {...createTooltipProps('end_date')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Latitude</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('latitude')}>
                          GPS latitude coordinate
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="number"
                        step="any"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleChange}
                        placeholder="e.g. 51.5074"
                        {...createTooltipProps('latitude')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Longitude</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('longitude')}>
                          GPS longitude coordinate
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="number"
                        step="any"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleChange}
                        placeholder="e.g. -0.1278"
                        {...createTooltipProps('longitude')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Area</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('area')}>
                          Geographic area designation
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                        {...createTooltipProps('area')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Route</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('route')}>
                          Delivery route designation
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="route"
                        value={formData.route}
                        onChange={handleChange}
                        {...createTooltipProps('route')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Depot</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('depot')}>
                          Used for availability checking, delivery planning, and equipment tracking
                        </Tooltip>
                      }
                    >
                      <Form.Select
                        name="depot"
                        value={formData.depot}
                        onChange={handleChange}
                        {...createTooltipProps('depot')}
                      >
                        <option value="">Select a depot...</option>
                        <MissingSelectOption
                          value={formData.depot}
                          options={depots}
                          getOptionValue={(depot) => depot.short_code}
                        />
                        {depots.map((depot) => (
                          <option key={depot.id} value={depot.short_code}>
                            {depot.depot_name}
                          </option>
                        ))}
                      </Form.Select>
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {customerSite ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function CustomerSites() {
  const [customerSites, setCustomerSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomerSite, setEditingCustomerSite] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchCustomerSites = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/customer-sites', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setCustomerSites(response.data.customerSites);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch customer sites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerSites();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomerSites(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchCustomerSites(newPage, searchTerm);
  };

  const handleAddCustomerSite = () => {
    setEditingCustomerSite(null);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleEditCustomerSite = (customerSite) => {
    console.log('Editing customer site:', customerSite);
    setEditingCustomerSite(customerSite);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleSaveCustomerSite = async (customerSiteData) => {
    console.log('Saving customer site data:', customerSiteData);

    try {
      if (editingCustomerSite) {
        console.log('Updating customer site ID:', editingCustomerSite.id);
        const response = await axios.put(
          `/api/customer-sites/${editingCustomerSite.id}`,
          customerSiteData
        );
        console.log('Update response:', response.data);
        setSuccess('Customer site updated successfully');
      } else {
        console.log('Creating new customer site');
        const response = await axios.post('/api/customer-sites', customerSiteData);
        console.log('Create response:', response.data);
        setSuccess('Customer site created successfully');
      }

      setShowModal(false);
      setEditingCustomerSite(null); // Clear editing state
      await fetchCustomerSites(pagination.page, searchTerm);
    } catch (error) {
      console.error('Error saving customer site:', error);

      // Throw the error so the modal can handle it and display it properly
      throw error;
    }
  };

  const handleDeleteCustomerSite = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer site?')) {
      try {
        await axios.delete(`/api/customer-sites/${id}`);
        setSuccess('Customer site deleted successfully');
        fetchCustomerSites(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete customer site');
      }
    }
  };

  const handleDeleteAllCustomerSites = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL customer sites? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your customer site records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/customer-sites');
          setSuccess(
            `All customer sites deleted successfully (${response.data.deletedCount} records)`
          );
          fetchCustomerSites(1, '');
        } catch (error) {
          setError('Failed to delete all customer sites');
        }
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <h1 className="mb-0">Customer Sites</h1>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button variant="primary" onClick={handleAddCustomerSite} className="me-2">
            Add Customer Site
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllCustomerSites}
            disabled={customerSites.length === 0}
          >
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by account no, site name, address, town, postcode, or telephone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading customer sites...</div>
          ) : customerSites.length === 0 ? (
            <div className="text-center p-3">No customer sites found.</div>
          ) : (
            <>
              <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
                  <thead>
                    <tr>
                      <th>Account No</th>
                      <th>Site Name</th>
                      <th>Address</th>
                      <th>Town</th>
                      <th>Postcode</th>
                      <th>Telephone</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerSites.map((customerSite) => (
                      <tr key={customerSite.id}>
                        <td>
                          <strong>{customerSite.account_no}</strong>
                        </td>
                        <td className="site-name">
                          <strong>{customerSite.site_name}</strong>
                        </td>
                        <td>
                          <div>
                            {customerSite.line_1 && <div>{customerSite.line_1}</div>}
                            {customerSite.line_2 && (
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {customerSite.line_2}
                              </div>
                            )}
                            {customerSite.line_3 && (
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {customerSite.line_3}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{customerSite.town}</td>
                        <td>{customerSite.postcode}</td>
                        <td>{customerSite.telephone}</td>
                        <td>
                          <Badge bg={customerSite.active ? 'success' : 'secondary'}>
                            {customerSite.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditCustomerSite(customerSite)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteCustomerSite(customerSite.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <CustomerSiteModal
          customerSite={editingCustomerSite}
          onClose={() => {
            setShowModal(false);
            setEditingCustomerSite(null);
            setError('');
            setSuccess('');
          }}
          onSave={handleSaveCustomerSite}
        />
      )}
    </Container>
  );
}

export default CustomerSites;
